import { toHash } from '@/Utils/StringUtility';
import { millisecondsInMinute } from '@/Utils/DateUtility';
import WebStorage, { storageTypes } from '@/Utils/WebStorage';

// Export cache duration constants for convenience
export const cacheDurationTypes = {
    shorter: 5 * 1000,   // 5 seconds
    short: 15 * 1000,    // 15 seconds
    medium: 60 * 1000,   // 1 minute
    long: 5 * 60 * 1000, // 5 minutes
    day: 86400000,       // 1 day
};

// Map to track pending promises for deduplication
const pendingPromises = new Map();

export function withCache(fn, options) {
    const {
        storageType = storageTypes.localStorage,
        key = toHash(fn.name),
        cacheDuration = cacheDurationTypes.shorter,
        skipCache = false,
    } = options || {};

    const storageKey = key;
    const expirationTime = Date.now() + cacheDuration;
    const storage = new WebStorage(storageKey, storageType);
    const cache = storage.get() || {};

    return (...args) => {
        const argsKey = toHash(JSON.stringify(args));
        const pendingKey = `${storageKey}:${argsKey}`;
        const cachedEntry = cache[argsKey];
        const currentTime = Date.now();

        // Remove expired entries from cache
        Object.keys(cache).forEach((cacheKey) => {
            if (cache[cacheKey] && cache[cacheKey].expiresAt < currentTime) {
                delete cache[cacheKey];
            }
        });

        if (cachedEntry && currentTime <= cachedEntry.expiresAt && !skipCache) {
            return Promise.resolve(cachedEntry);
        }

        // If there is already a pending promise for these arguments, return it
        if (pendingPromises.has(pendingKey)) {
            return pendingPromises.get(pendingKey);
        }

        // Create a new promise and store it in pendingPromises
        const promise = (async () => {
            try {
                const result = await fn.apply(this, args);
                result.expiresAt = expirationTime;
                cache[argsKey] = result;
                storage.set(cache);
                return result;
            }
            finally {
                // Remove pending promise after completion
                pendingPromises.delete(pendingKey);
            }
        })();

        pendingPromises.set(pendingKey, promise);
        return promise;
    };
}

export async function asyncTaskWithCache(serviceFunction, cacheOptions, storage = storageTypes.sessionStorage) {
    const defaultOptions = { key: 'cacheKey', cacheTime: 5 };
    const options = { ...defaultOptions, ...cacheOptions };

    const cache = new WebStorage(options.key, storage);
    const cachedEntry = cache.get();

    if (cachedEntry && cachedEntry.expiresAt >= Date.now()) {
        return cachedEntry.value;
    }
    const result = await serviceFunction();
    cache.set({
        value: result,
        expiresAt: Date.now() + options.cacheTime * millisecondsInMinute,
    });
    return result;
}